import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, CalendarDays } from 'lucide-react';
import Button from '../../../components/ui/Button';

const DAYS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TrainerExceptionsModal = ({ isOpen, onClose, departmentId, trainers }) => {
    const [exceptions, setExceptions] = useState([]);
    const [selectedTrainer, setSelectedTrainer] = useState('');
    const [day, setDay] = useState(1);
    const [startTime, setStartTime] = useState('08:00');
    const [endTime, setEndTime] = useState('12:00');
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (isOpen && departmentId) {
            try {
                const stored = JSON.parse(localStorage.getItem(`trainerExceptions_${departmentId}`) || '[]');
                setExceptions(stored);
            } catch (e) {
                setExceptions([]);
            }
        }
    }, [isOpen, departmentId]);

    const saveExceptions = (newExceptions) => {
        localStorage.setItem(`trainerExceptions_${departmentId}`, JSON.stringify(newExceptions));
        setExceptions(newExceptions);
    };

    const handleAdd = () => {
        if (!selectedTrainer || !startTime || !endTime) return;
        const newEx = {
            id: `ex_${Date.now()}`,
            trainer_id: selectedTrainer,
            day_of_week: Number(day),
            start_time: startTime,
            end_time: endTime,
            reason: reason || 'Unavailable'
        };
        saveExceptions([...exceptions, newEx]);
        setSelectedTrainer('');
        setReason('');
    };

    const handleDelete = (id) => {
        saveExceptions(exceptions.filter(e => e.id !== id));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                        <CalendarDays className="h-5 w-5 text-blue-600" />
                        <h2 className="text-lg font-bold text-slate-800">Trainer Availability & Exceptions</h2>
                    </div>
                    <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-6 bg-slate-50">
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                        <h3 className="text-sm font-semibold mb-3">Add Blocked Period</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-slate-700 mb-1">Trainer</label>
                                <select 
                                    value={selectedTrainer} 
                                    onChange={e => setSelectedTrainer(e.target.value)}
                                    className="w-full text-sm border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                                >
                                    <option value="">-- Select Trainer --</option>
                                    {trainers.map(t => (
                                        <option key={t.id} value={t.id}>{t.full_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-slate-700 mb-1">Day</label>
                                <select 
                                    value={day} 
                                    onChange={e => setDay(e.target.value)}
                                    className="w-full text-sm border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                                >
                                    {DAYS.map((d, i) => i > 0 && <option key={i} value={i}>{d}</option>)}
                                </select>
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-xs font-medium text-slate-700 mb-1">Start Time</label>
                                <input 
                                    type="time" 
                                    value={startTime} 
                                    onChange={e => setStartTime(e.target.value)}
                                    className="w-full text-sm border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-xs font-medium text-slate-700 mb-1">End Time</label>
                                <input 
                                    type="time" 
                                    value={endTime} 
                                    onChange={e => setEndTime(e.target.value)}
                                    className="w-full text-sm border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-slate-700 mb-1">Reason (Optional)</label>
                                <input 
                                    type="text" 
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    placeholder="e.g. Part-time schedule"
                                    className="w-full text-sm border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 p-2 border"
                                />
                            </div>
                        </div>
                        <Button onClick={handleAdd} disabled={!selectedTrainer || !startTime || !endTime} className="w-full h-8 text-xs">
                            <Plus className="h-4 w-4 mr-1" /> Add Blocked Period
                        </Button>
                    </div>

                    <div>
                        <h3 className="text-sm font-semibold mb-3">Current Exceptions ({exceptions.length})</h3>
                        {exceptions.length === 0 ? (
                            <p className="text-sm text-slate-500 italic">No exceptions defined for this department.</p>
                        ) : (
                            <div className="space-y-2">
                                {exceptions.map(ex => {
                                    const trainer = trainers.find(t => String(t.id) === String(ex.trainer_id));
                                    return (
                                        <div key={ex.id} className="bg-white p-3 rounded border shadow-sm flex items-center justify-between">
                                            <div>
                                                <div className="font-semibold text-sm text-slate-900">{trainer ? trainer.full_name : 'Unknown Trainer'}</div>
                                                <div className="text-xs text-slate-600 mt-0.5">
                                                    {DAYS[ex.day_of_week]} • {ex.start_time} - {ex.end_time} {ex.reason && `• ${ex.reason}`}
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => handleDelete(ex.id)}
                                                className="text-rose-500 hover:bg-rose-50 p-2 rounded transition-colors"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-4 border-t bg-white flex justify-end">
                    <Button variant="outline" onClick={onClose} className="px-6">Close</Button>
                </div>
            </div>
        </div>
    );
};

export default TrainerExceptionsModal;
